// Libs
import React, { useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import * as queryString from "query-string";
import { bindActionCreators } from "redux";

// Components
import TopBar from "../ui/TopBar";
import Page from "../ui/Page";
import ActionWrapper from "../ui/ActionWrapper";
import RegistrationPosts from "./RegistrationPosts";
import RegistrationFormBuilder from "./RegistrationFormBuilder";

// Styles
import { PlusIcon } from "mdi-react";

// Utilities and config
import { feedTypes, localStorageAction, postLayoutModes } from "./config";
import getPageFromId from "../../utilities/get-page-from-id";
import registrationLocalStorageHandler from "../../utilities/use-registration-local-storage";

// Actions
import { getRegistrationPosts, resetAll } from "../../actions/registrationActions";
import { addToast, hideDialog, hideModalPage, showDialog, showModalPage } from "../../actions/uiActions";
import req from "../../utilities/request-utility";
import ScrollView from "../ui/ScrollView";

function isAccordionLayout(matchObject) {
  if (matchObject.path.match(/my-registration-accordion/gi)) {
    return true;
  } else {
    return false;
  }
}

function enableNotifications(matchObject) {
  if (matchObject.path.match(/my-registration-accordion/gi)) {
    return true;
  } else {
    return false;
  }
}

const RegistrationOverview = (props) => {
  // Page for navigation
  const { match } = props;
  const pages = useSelector((state) => state.pages.pages);
  const [page] = useState(getPageFromId(pages, match.params.pageId));
  const [registrationConfig, setRegistrationConfig] = useState({});

  const LAYOUT_MODE = isAccordionLayout(match) ? postLayoutModes.accordion : postLayoutModes.post;
  const ENABLE_NOTIFICATIONS = enableNotifications(match) ? postLayoutModes.accordion : postLayoutModes.post;

  // Multi-language handling
  const lang = useSelector((state) => state.language.language);

  // Local storage variable
  const [doesLocalStorageExist, setDoesLocalStorageExist] = useState(false);

  // Get the registrationId / dataId
  let { dataId: registrationId } = queryString.parse(window.location.search);

  // Initial componentDidMount load the posts for the registration
  useEffect(() => {
    props.resetAll();
    getRegistrationConfiguration(registrationId);
    requestAnimationFrame(() => {
      props.getRegistrationPosts({ registrationId, feedType: feedTypes.mine });
      isThereARegistrationFormSaved();
    });
    // eslint-disable-next-line
  }, []);

  // Delete the saved registration
  const deleteLocaleRegistration = () => {
    try {
      registrationLocalStorageHandler(localStorageAction.delete, { id: registrationId });
    } catch (err) {
      throw err;
    }
  };

  const isThereARegistrationFormSaved = () => {
    try {
      let registrationFields = localStorage.getItem(`registration-fields-${registrationId}`);
      registrationFields = JSON.parse(registrationFields);
      if (Array.isArray(registrationFields) && registrationFields.length > 0) {
        setDoesLocalStorageExist(true);
      } else {
        setDoesLocalStorageExist(false);
      }
    } catch (err) {
      throw err;
    }
  };

  function refreshContent() {
    dispatch(getRegistrationPosts({ registrationId, feedType: feedTypes.mine, offset: 0 }));
  }

  function onCreateNewRegistrationClick() {
    if (doesLocalStorageExist) {
      props.showDialog({
        title: lang.hello,
        content: lang.continueFromLastCheckDescription,
        primaryActionTitle: lang.yesContinueFromLastCheck,
        primaryAction: () => {
          props.showModalPage({
            title: `${page.title}`,
            closeCallback: () => {
              isThereARegistrationFormSaved();
              props.hideModalPage();
            },
            content: <RegistrationFormBuilder loadFromLocaleStorage={true} postSubmitCallback={refreshContent} />,
            useScrollView: false,
          });
        },
        secondaryAction: () => {
          props.showModalPage({
            title: `${page.title}`,
            closeCallback: () => {
              isThereARegistrationFormSaved();
              props.hideModalPage();
            },
            content: <RegistrationFormBuilder loadFromLocaleStorage={false} postSubmitCallback={refreshContent} />,
            useScrollView: false,
          });
          props.hideDialog();
          deleteLocaleRegistration();
        },
        secondaryActionTitle: lang.createNewForm,
      });
    } else {
      props.showModalPage({
        title: `${page.title}`,
        closeCallback: () => {
          isThereARegistrationFormSaved();
          props.hideModalPage();
        },
        content: <RegistrationFormBuilder loadFromLocaleStorage={false} postSubmitCallback={refreshContent} />,
        useScrollView: false,
      });
    }
  }

  function getRegistrationConfiguration(registrationId) {
    // req.... -> then
    req()(`registration/${registrationId}/config`)
      .then(({ data }) => setRegistrationConfig(data))
      .catch((err) =>
        // If api fails it will revert back to default "Like" etc. Its better than nothing
        setRegistrationConfig({
          likeTextIdentifiers: {
            youLikeThis: "youLikeThis",
            youAndOneOtherLikeThis: "youAnd__placeholder__other",
            youAndMultipleOthersLikeThis: "youAnd__placeholder__others",
            oneOrManyOtherLikeThis: "__placeholder__likeThis",
          },
        })
      );
  }

  return (
    <Page>
      <TopBar
        title={page.title}
        useDefaultBackButton={true}
        actionRight={
          <ActionWrapper data-test-id="btn-show-add-post-modal" onClick={onCreateNewRegistrationClick}>
            <PlusIcon />
          </ActionWrapper>
        }
      />

      <ScrollView onScrollEnd={() => props.getRegistrationPosts({ registrationId, feedType: feedTypes.mine })}>
        <RegistrationPosts
          layoutMode={LAYOUT_MODE}
          feedType={feedTypes.mine}
          registrationConfig={registrationConfig}
          match={match}
        />
      </ScrollView>
    </Page>
  );
};

const mapDispatchToProps = (dispatch) => ({
  hideDialog: bindActionCreators(hideDialog, dispatch),
  showDialog: bindActionCreators(showDialog, dispatch),
  addToast: bindActionCreators(addToast, dispatch),
  hideModalPage: bindActionCreators(hideModalPage, dispatch),
  showModalPage: bindActionCreators(showModalPage, dispatch),
  getRegistrationPosts: bindActionCreators(getRegistrationPosts, dispatch),
  resetAll: bindActionCreators(resetAll, dispatch),
});

export default connect(null, mapDispatchToProps)(RegistrationOverview);
